/**
 * =============================================================================
 * AdminRoute - Route guard for admin pages
 * =============================================================================
 *
 * Wraps admin routes so access control happens in ONE place instead of in each
 * page component. Prevents the two issues we had:
 *
 * 1. Slow render: the role check is cached in useAdminRole, so navigating
 *    between admin pages no longer re-runs getSession() + has_role().
 * 2. False "Åtkomst nekad": we never redirect while the role check is still
 *    resolving, and we keep the last known admin state during a session
 *    refresh instead of momentarily treating the user as unauthorized.
 * =============================================================================
 */

import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useToast } from "@/hooks/use-toast";

const AdminRoute = () => {
  const { isAdmin, isVerifiedAdmin, loading } = useAdminRole();
  const { toast } = useToast();
  const location = useLocation();
  const notified = useRef(false);

  const denied = !loading && !isAdmin;
  // Admin role but expired/missing 2FA session -> re-verify instead of "Åtkomst nekad".
  const needsVerification = !loading && isAdmin && !isVerifiedAdmin;

  useEffect(() => {
    if (denied && !notified.current) {
      notified.current = true;
      toast({
        title: "Åtkomst nekad",
        description: "Du har inte behörighet att visa denna sida.",
        variant: "destructive",
      });
    }
    if (!denied) {
      notified.current = false;
    }
  }, [denied, toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
          role="status"
          aria-label="Laddar"
        />
      </div>
    );
  }

  if (denied) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  if (needsVerification) {
    return <Navigate to="/admin/verify" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
};

export default AdminRoute;
